import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { freeSearchConfig } from "../../../enums/filtersType";
import { selectEntitiesByFilters } from "../../../store/views/entityView";
import { getDiscoveryBaseFilters } from "../../../utils/entitiesFiltersUtils";
import { buildDiscoveryFilterState } from "./discoveryFiltersUtils";
import useDiscoveryFilterTemplates from "./useDiscoveryFilterTemplates";
import DiscoveryGalleries from "./discoveryGalleries/DiscoveryGalleries";
import DiscoverySearchResults from "./discoverySearchResults/DiscoverySearchResults";
import DiscoveryFilterBar from "./discoveryFilterBar/DiscoveryFilterBar";
import Page from "../../ui/page/Page";
import pathRouts from "../../../enums/pathRoute";
import iconsList from "../../ui/icons/iconsList";
import classes from "./DiscoveryPage.module.css";

const DiscoveryPage = () => {
    const navigate = useNavigate();
    const { texts, lang } = useSelector((state) => state.translate);
    const [searchParams, setSearchParams] = useSearchParams();
    const filterTemplates = useDiscoveryFilterTemplates();
    const courses = useSelector((state) =>
        selectEntitiesByFilters(state, getDiscoveryBaseFilters(state.auth.user?.studentkita))
    );

    const activeFilters = useMemo(
        () => buildDiscoveryFilterState(searchParams, filterTemplates, courses, { language: lang }),
        [searchParams, filterTemplates, courses, lang]
    );

    const isDefaultFilterValue = (filterName, value) => filterName === "language" && value === lang;

    const hasUserFilters = useMemo(() => {
        const hasFreeSearch = !!searchParams.get(freeSearchConfig.param)?.trim();
        const hasAnyFilter = filterTemplates.some(f => {
            const value = searchParams.get(f.name);
            return value !== null && !isDefaultFilterValue(f.name, value);
        });
        return hasFreeSearch || hasAnyFilter;
    }, [searchParams, filterTemplates, lang]);

    const updateSearchParam = (key, value) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value?.trim())
                next.set(key, value.trim());
            else
                next.delete(key);
            return next;
        }, { replace: true });
    };

    const handleSelectFilter = (filterKey, option) => {
        updateSearchParam(filterKey, option.id.toString());
    };

    const handleApplyFilters = (selections) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            filterTemplates.forEach((filter) => {
                const value = selections?.[filter.name]?.toString();
                if (value && !isDefaultFilterValue(filter.name, value))
                    next.set(filter.name, value);
                else
                    next.delete(filter.name);
            });
            return next;
        }, { replace: true });
    };

    const handleFreeSearch = (value) => {
        updateSearchParam(freeSearchConfig.param, value);
    };

    const handleClearFilters = () => {
        setSearchParams({}, { replace: true });
    };


    return (
        <Page
            title={texts.discovery}
            icon={iconsList.discovery}
            onBack={() => navigate(`/${pathRouts.HOME}`)}
        >
            <div className={classes.DiscoveryPage}>
                <div className={classes.DiscoveryLayout}>
                    <div className={classes.LeftSection}>
                        <DiscoveryFilterBar
                            courses={courses}
                            onFreeSearch={handleFreeSearch}
                            onSelectFilter={handleSelectFilter}
                            onApplyFilters={handleApplyFilters}
                            onClear={handleClearFilters}
                        />
                        {hasUserFilters ? (
                            <DiscoverySearchResults filters={activeFilters} />
                        ) : (
                            <DiscoveryGalleries />
                        )}
                    </div>
                </div>
            </div>
        </Page>
    );
};

export default DiscoveryPage;
